import React, { useState, useRef, useEffect } from "react";

export default function LanguageSwitcher({ languages, currentSurveyId, groupId }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Find current language
    const currentLanguage = languages.find(lang => lang.survey_id === currentSurveyId) || languages[0];

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const handleLanguageSwitch = (newSurveyId) => {
        if (newSurveyId === currentSurveyId) {
            setIsOpen(false);
            return;
        }

        // Get current URL search params to preserve UTM parameters and lid
        const currentParams = new URLSearchParams(window.location.search);
        const paramsString = currentParams.toString();
        
        // Navigate to new survey with same group_id and preserve query params
        const newPath = `/G/${groupId}/${newSurveyId}${paramsString ? `?${paramsString}` : ''}`;
        window.location.href = newPath; // Use window.location to force reload
        setIsOpen(false);
    };

    if (!languages || languages.length <= 1) {
        return null; // Don't show dropdown if only one language or no languages
    }

    return (
        <div className="language-switcher-container" ref={dropdownRef}>
            <button
                className="language-switcher-button"
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <svg className="globe-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.66669 10C6.66669 14.6024 8.15907 18.3333 10 18.3333C11.841 18.3333 13.3334 14.6024 13.3334 10C13.3334 5.39763 11.841 1.66667 10 1.66667C8.15907 1.66667 6.66669 5.39763 6.66669 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1.66669 10H18.3334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="language-name">{currentLanguage?.language_name}</span>
                <svg className="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {isOpen && (
                <div className="language-dropdown-menu">
                    {languages.map((lang) => (
                        <button
                            key={lang.survey_id}
                            className={`language-dropdown-item ${lang.survey_id === currentSurveyId ? 'active' : ''}`}
                            type="button"
                            onClick={() => handleLanguageSwitch(lang.survey_id)}
                        >
                            <span className="language-name">{lang.language_name}</span>
                            {lang.is_primary && (
                                <span className="primary-badge">Primary</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

